<template>
    <ul class="m-0 flex list-none flex-col gap-0.5 p-0">
        <li
            v-for="bookmark in bookmarks"
            :key="bookmark.uuid"
            class="bookmark-row group flex items-center gap-1 rounded-lg pr-2 transition-colors"
        >
            <button
                type="button"
                class="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 border-none bg-transparent py-2.5 pl-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent"
                @click="navigate(bookmark)"
            >
                <svg-icon class="shrink-0 text-theme-accent" icon="baseline-bookmark-24px" width="20px" height="20px" />
                <span class="truncate text-[13px] font-medium text-theme-text">{{ label(bookmark) }}</span>
            </button>
            <button
                type="button"
                class="bookmark-delete flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded border-none bg-transparent text-theme-toolbar-icon transition hover:text-theme-text focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent"
                :class="
                    onlyMobileVisible ? 'opacity-100' : 'opacity-0 focus-visible:opacity-100 group-hover:opacity-100'
                "
                :aria-label="localizations[lang].delete"
                :title="localizations[lang].delete"
                @click="confirmDelete(bookmark)"
            >
                <svg-icon icon="close" width="20px" height="20px" />
            </button>
        </li>
    </ul>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

export default {
    name: 'BookmarkList',
    props: {
        bookmarks: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        ...mapState(['localizations', 'lang']),
        ...mapGetters(['isEpub', 'onlyMobileVisible']),
    },
    mounted() {
        this.$bus.$on('confirmDeleteBookmark', this.handleConfirmDelete);
    },
    beforeDestroy() {
        this.$bus.$off('confirmDeleteBookmark', this.handleConfirmDelete);
    },
    methods: {
        ...mapActions(['goToPage', 'goToLocation', 'toggleNotebook', 'deleteBookmark']),
        ...mapMutations(['openConfirmationModal']),
        /**
         * The list label: "Page {n}".
         * @param {Object} bookmark
         * @returns {string}
         */
        label(bookmark) {
            const template = this.localizations[this.lang].bookmarkPageLabel;
            return template.replace('{page}', bookmark.page ?? '');
        },
        /**
         * Navigates the reader to the bookmark's page (PDF) or locator (EPUB).
         * @param {Object} bookmark
         */
        navigate(bookmark) {
            if (this.isEpub) {
                this.goToLocation({ epubR2Location: bookmark.locator });
            } else {
                this.goToPage({ page: bookmark.page, trigger: 'click', source: 'bookmarks' });
            }

            if (this.onlyMobileVisible) {
                this.toggleNotebook();
            }
        },
        /**
         * Opens the shared confirmation modal before deleting a bookmark. The actual delete
         * runs from handleConfirmDelete once the user confirms (via the $bus event), mirroring
         * how flashcards/notes/highlights gate their deletions.
         * @param {Object} bookmark - The bookmark to delete
         */
        confirmDelete(bookmark) {
            this.openConfirmationModal({
                title: this.localizations[this.lang].confirmDeleteBookmark.title,
                message: this.localizations[this.lang].confirmDeleteBookmark.message,
                onConfirmAction: 'confirmDeleteBookmark',
                context: { uuid: bookmark.uuid },
            });
        },
        /**
         * Deletes the bookmark once the user confirms in the modal.
         * @param {Object} context - The confirmation context emitted by the modal
         * @param {string} context.uuid - The bookmark uuid to delete
         */
        handleConfirmDelete({ uuid }) {
            this.deleteBookmark({ uuid });
        },
    },
};
</script>

<style scoped>
/*
 * Subtle, theme-adaptive hover shades. The prototype uses hardcoded rgba(0,0,0,.04)/.08,
 * which would be invisible on the dark / high-contrast themes; deriving the overlay from
 * the theme text color keeps the same subtle two-level effect while adapting per theme.
 */
.bookmark-row:hover {
    background-color: color-mix(in srgb, var(--theme-text-color) 5%, transparent);
}

.bookmark-delete:hover {
    background-color: color-mix(in srgb, var(--theme-text-color) 10%, transparent);
}
</style>
